import { ExportFileType } from '@common/ts/enums';
import { buildUrlWithParams } from '@common/utils';
import { ECategoryMode, TranscriptsView } from '@/enums';
import { FiltersConfig, PaginationConfig, ParamsConfig } from '@/types';

export const API_ENDPOINTS = {
  ORGANIZATIONS: {
    GET_BY_ID: (organizationId?: string) => `/organizations/${organizationId}`,
    UPDATE: (organizationId?: string) => `/organizations/${organizationId}`,
    DELETE: (organizationId: string) => `/organizations/${organizationId}`,
    GET_ALL: '/organizations',
    GET_MEMBERS: (
      organizationId?: string,
      pagination?: PaginationConfig,
      filters?: FiltersConfig,
    ) => {
      const base = `/organizations/${organizationId}/members`;
      const params: ParamsConfig = { ...pagination, ...filters };
      return buildUrlWithParams(base, params);
    },
    REMOVE_MEMBER: (organizationId?: string) => `/organizations/${organizationId}/members`,
    UPDATE_MEMBER_ROLE: (organizationId?: string) => `/organizations/${organizationId}/members`,
    INVITE_USER: (organizationId?: string) => `/organizations/${organizationId}/invite`,
    CANCEL_INVITATION: (organizationId?: string) => `/organizations/${organizationId}/invite`,
    VALIDATE_INVITE: (organizationId?: string) =>
      `/organizations/${organizationId}/validate_invite`,
    NOTIFY_INVITER: (organizationId?: string) => `/organizations/${organizationId}/notify_inviter`,
    RESEND_INVITATION: (organizationId?: string) =>
      `/organizations/${organizationId}/resend_invitation`,
    TRANSFER_OWNERSHIP: (organizationId?: string) =>
      `/organizations/${organizationId}/transfer_ownership`,
  },

  WORKSPACES: {
    CREATE: (organizationId?: string) => `/organizations/${organizationId}/workspaces`,
    GET_ALL: (organizationId?: string) => `/organizations/${organizationId}/workspaces`,
    GET_BY_ID: (organizationId?: string, workspaceId?: string) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}`,
    UPDATE: (organizationId?: string, workspaceId?: string) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}`,
    DELETE: (organizationId?: string, workspaceId?: string) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}`,
    GET_MEMBERS: (organizationId?: string, workspaceId?: string) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}/members`,
    REMOVE_MEMBER: (organizationId?: string, workspaceId?: string, userId?: string) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}/members/${userId}`,
    UPDATE_ROLE: (organizationId?: string, workspaceId?: string) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}/role`,
    SHARE: (organizationId?: string, workspaceId?: string) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}/share`,
    TRANSFER_OWNERSHIP: (organizationId?: string, workspaceId?: string) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}/transfer_ownership`,
  },

  PROJECTS: {
    GET_ALL: (
      organizationId?: string,
      workspaceId?: string,
      pagination?: PaginationConfig,
      filters?: FiltersConfig,
    ) => {
      const base = `/organizations/${organizationId}/workspaces/${workspaceId}/projects`;
      const params: ParamsConfig = { ...pagination, ...filters };
      return buildUrlWithParams(base, params);
    },
    CREATE: (organizationId?: string, workspaceId?: string) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}/projects`,
    GET_BY_ID: (organizationId?: string, workspaceId?: string, projectId?: string) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}/projects/${projectId}`,
    UPDATE: (organizationId?: string, workspaceId?: string, projectId?: string) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}/projects/${projectId}`,
    DELETE: (organizationId?: string, workspaceId?: string, projectId?: string) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}/projects/${projectId}`,
    COMPLETE: (organizationId?: string, workspaceId?: string, projectId?: string) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}/projects/${projectId}/complete`,
    MOVE: (organizationId?: string, workspaceId?: string, projectId?: string) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}/projects/${projectId}/move`,
    COPY: (organizationId?: string, workspaceId?: string, projectId?: string) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}/projects/${projectId}/copy`,
    PUBLISH: (organizationId?: string, workspaceId?: string, projectId?: string) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}/projects/${projectId}/publish`,
    REQUEST_PUBLISH: (organizationId?: string, workspaceId?: string, projectId?: string) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}/projects/${projectId}/request_publish`,
    APPROVE_PUBLISH: (organizationId?: string, workspaceId?: string, projectId?: string) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}/projects/${projectId}/approve_publish`,
    UPLOAD_MEDIA: (
      organizationId?: string,
      workspaceId?: string,
      projectId?: string,
      questionId?: string,
    ) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}/projects/${projectId}/media/${questionId}`,
    DELETE_MEDIA: (
      organizationId?: string,
      workspaceId?: string,
      projectId?: string,
      questionId?: string,
    ) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}/projects/${projectId}/media/${questionId}`,
    RENAME: (organizationId?: string, workspaceId?: string, projectId?: string) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}/projects/${projectId}/rename`,
    UNPUBLISH: (organizationId?: string, workspaceId?: string, projectId?: string) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}/projects/${projectId}/unpublish`,
    REPUBLISH: (organizationId?: string, workspaceId?: string, projectId?: string) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}/projects/${projectId}/republish`,
    GET_CONVERSATION_COUNTS: (organizationId?: string, workspaceId?: string, projectId?: string) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}/projects/${projectId}/conversations/counts`,
    GET_THEME: (projectId?: string) => `/projects/${projectId}/theme`,
    GET_CONSENT_SCREEN: (projectId?: string) => `/projects/${projectId}/consent_screen`,
  },

  USERS: {
    REGISTER: '/users/register',
    VERIFY_EMAIL: '/users/verify_email',
    ACCEPT_TELLET_INVITE: (organizationId?: string) =>
      `/users/accept_tellet_invite/${organizationId}`,
    ACCEPT_ORGANIZATION_INVITE: (organizationId?: string) =>
      `/users/accept_organization_invite/${organizationId}`,
    LOGIN: '/users/login',
    RESEND_VERIFICATION_EMAIL: '/users/resend_verification_email',
    GET_USER_DATA: '/users',
    UPDATE_PROFILE: '/users',
    DELETE_ACCOUNT: '/users',
    FORGOT_PASSWORD: '/users/forgot_password',
    RESET_PASSWORD: '/users/reset_password',
    CHANGE_PASSWORD: '/users/change_password',
    VERIFY_2FA: '/users/verify-2fa',
    GENERATE_2FA: '/users/generate-2fa',
    ENABLE_2FA: '/users/enable-2fa',
    DISABLE_2FA: '/users/disable-2fa',
  },
  THEMES: {
    GET_BY_ID: (organizationId: string, themeId: string | null) =>
      `/organizations/${organizationId}/themes/${themeId}`,
    UPDATE: (organizationId?: string, themeId?: string) =>
      `/organizations/${organizationId}/themes/${themeId}`,
    DELETE: (organizationId?: string, themeId?: string) =>
      `/organizations/${organizationId}/themes/${themeId}`,
    GET_ALL: (organizationId?: string, pagination?: PaginationConfig, filters?: FiltersConfig) => {
      const base = `/organizations/${organizationId}/themes`;
      const params: ParamsConfig = { ...pagination, ...filters };
      return buildUrlWithParams(base, params);
    },
    CREATE: (organizationId?: string) => `/organizations/${organizationId}/themes`,
  },
  RESULTS: {
    GET_OVERALL: (projectId?: string, filterId?: string | null) =>
      `analyzer/results/${projectId}/overall${filterId ? `?filter_id=${filterId}` : ''}`,
    GET_INTERVIEW_QUESTIONS: (projectId: string, filterId: string | null) =>
      `analyzer/results/${projectId}/interview_questions${
        filterId ? `?filter_id=${filterId}` : ''
      }`,
    GET_PER_QUESTION_INSIGHTS: (projectId: string, questionId: string, filterId: string | null) =>
      `analyzer/results/${projectId}/perquestion/${questionId}/insights${
        filterId ? `?filter_id=${filterId}` : ''
      }`,
    GET_PER_QUESTION_TRANSCRIPTS: (
      projectId?: string,
      questionId?: string,
      categoryType?: ECategoryMode | null,
      filterId?: string | null,
      language?: string | null,
      pagination?: PaginationConfig,
    ) => {
      const base = `analyzer/results/${projectId}/perquestion/${questionId}/transcripts`;
      return buildUrlWithParams(base, {
        filter_id: filterId,
        category_type: categoryType,
        language,
        ...pagination,
      });
    },
    GET_CONVERSATIONS: (
      projectId?: string,
      filterId?: string | null,
      language?: string | null,
      conversationFilter?: TranscriptsView,
      pagination?: PaginationConfig,
      searchFilters?: FiltersConfig,
    ) => {
      const base = `analyzer/results/${projectId}/conversations`;
      return buildUrlWithParams(base, {
        filter_id: filterId,
        language,
        conversationFilter,
        ...pagination,
        ...searchFilters,
      });
    },
    GET_CONVERSATION_BY_ID: (projectId?: string, conversationId?: string) =>
      `analyzer/results/${projectId}/conversations/${conversationId}`,
    DELETE_CONVERSATIONS: (projectId?: string) => `analyzer/results/${projectId}/conversations`,
    MARK_CONVERSATIONS_COMPLETE: (projectId?: string) =>
      `analyzer/results/${projectId}/conversations/mark-complete`,
    GET_LANGUAGES: (projectId?: string) => `analyzer/results/${projectId}/languages`,
    GET_TRANSLATION_PROGRESS: (projectId?: string, language?: string) =>
      `analyzer/results/${projectId}/languages/${language}/progress`,
    TRANSLATE_CONVERSATIONS: (projectId?: string) => `analyzer/results/${projectId}/languages`,
    EXPORT_CONVERSATION: (projectId?: string, conversationId?: string, fileType?: ExportFileType) =>
      buildUrlWithParams(
        `analyzer/results/single_conversation_details/export/${projectId}/${conversationId}`,
        {
          file_type: fileType,
        },
      ),
    EXPORT_CONVERSATION_DETAILS: (
      projectId: string,
      filterId: string | null,
      fileType?: ExportFileType,
      transcriptsView?: TranscriptsView,
    ) => {
      return buildUrlWithParams(`analyzer/results/conversations_details/export/${projectId}`, {
        file_type: fileType,
        filter_id: filterId,
        conversationFilter: transcriptsView,
      });
    },
    EXPORT_CONVERSATION_OVERVIEW: (
      projectId: string,
      filterId: string | null,
      fileType?: ExportFileType,
      transcriptsView?: TranscriptsView,
    ) => {
      return buildUrlWithParams(`analyzer/results/conversations_overview/export/${projectId}`, {
        file_type: fileType,
        filter_id: filterId,
        conversationFilter: transcriptsView,
      });
    },
    EXPORT_FULL_ANSWERS_TRANSCRIPT: (
      projectId: string,
      filterId: string | null,
      fileType?: ExportFileType,
      transcriptsView?: TranscriptsView,
    ) => {
      return buildUrlWithParams(`analyzer/results/questions-columns/export/${projectId}`, {
        file_type: fileType,
        filter_id: filterId,
        conversationFilter: transcriptsView,
      });
    },
    EXPORT_ANALYSIS: (projectId: string, filterId?: string | null) => {
      return buildUrlWithParams(`analyzer/results/analysis/export/${projectId}`, {
        filter_id: filterId,
      });
    },
    EXPORT_CONVERSATION_MEDIA: (projectId: string, conversationId: string) =>
      `analyzer/results/media/export/${projectId}/${conversationId}`,
  },
  RUN: {
    START_ANALYSIS: (projectId?: string, filter_id?: string) =>
      `analyzer/run/${projectId}${filter_id ? `?filter_id=${filter_id}` : ''}`,
    GET_RUN_STATE: (projectId?: string) => `analyzer/run/${projectId}/state`,
  },
  AI_SEARCH: {
    GET: (projectId?: string, filterId?: string | null) =>
      `analyzer/aisearch/${projectId}${filterId ? `?filter_id=${filterId}` : ''}`,
    CREATE: (projectId?: string) => `analyzer/aisearch/${projectId}`,
    DELETE: (projectId?: string) => `analyzer/aisearch/${projectId}`,
  },
  CATEGORIES: {
    GENERATE: (projectId?: string, categoryType?: string, conversationsNumber?: number) =>
      `analyzer/categorization/${projectId}?category_type=${categoryType}&conversations_num=${conversationsNumber}`,
    GENERATE_AI: (projectId?: string, questionId?: string) =>
      `analyzer/categorization/${projectId}/ai?question_id=${questionId}`,
    GET_BY_QUESTION: (projectId?: string, questionId?: string, filter_id?: string | null) =>
      `analyzer/categorization/${projectId}/question/${questionId}${
        filter_id ? `?filter_id=${filter_id}` : ''
      }`,
    DELETE_BY_QUESTION: (projectId?: string, questionId?: string) =>
      `analyzer/categorization/${projectId}/question/${questionId}`,
    UPDATE_BY_QUESTION: (projectId?: string, questionId?: string) =>
      `analyzer/categorization/${projectId}/question/${questionId}`,
  },
  NOTIFICATIONS: {
    GET_PREFERENCES: '/notifications/preferences',
    UPDATE_PREFERENCES: '/notifications/preferences',
  },
  FILTERS: {
    CREATE: (projectId?: string) => `analyzer/filters/${projectId}`,
    GET_BY_ID: (projectId?: string) => `analyzer/filters/${projectId}`,
    DELETE: (projectId?: string, filterId?: string) => `analyzer/filters/${projectId}/${filterId}`,
    UPDATE: (projectId?: string, filterId?: string) => `analyzer/filters/${projectId}/${filterId}`,
    GET_METADATA: (projectId?: string) => `analyzer/filters/${projectId}/metadata`,
  },
};
